import {javaApiFor} from 'sources/api';
import {List} from 'immutable';
import {
  KpiObservation,
  KpiObservationId,
  KpiRequest,
  KpiTargetId,
  CoachingKpi,
} from '@store/@types/kpi';
import {Double, Int} from '@store/@types/util';
import {UserId} from 'records/User';
import {Moment} from 'moment';
import {SortDirection} from 'utils/const';
import moment from 'moment';

const DATE_FORMAT = 'YYYY-MM-DD';
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE = 1;
const DEFAULT_SORT = SortDirection.DESC;

const observationApi = javaApiFor(`/profile/kpi_observations`);
const reminderApi = javaApiFor(`/profile/kpi_reminders`);

export interface ObservationSearchRequest {
  userId: UserId;
  startDate: Moment;
  endDate?: Moment;
  search?: string;
  page?: Int;
  count?: Int;
  sortDirection?: SortDirection;
}

interface ObservationSearchResponse {
  results: KpiObservation[];
  total: Int;
}

export interface ObservationBySkillsRequest {
  userId: string;
  startDate: Moment;
  endDate?: Moment | null;
}

type ObservationBySkillsResponse = KpiObservation[];

export interface CreateObservationRequest {
  userId: UserId;
  kpiId: KpiTargetId;
  value: Double;
}

export interface UpdateObservationRequest {
  value: Double;
}

export default class KpiObservationSource {
  static search(params: ObservationSearchRequest): Promise<{
    userId: UserId;
    total: Int;
    kpiObservations: List<KpiObservation>;
  }> {
    const endDate = params.endDate ?? moment();
    return observationApi
      .getJson<ObservationSearchResponse>('/search', {
        params: {
          ...params,
          startDate: params.startDate.format(DATE_FORMAT),
          endDate: endDate.clone().add(2, 'days').format(DATE_FORMAT),
          count: params.count || DEFAULT_PAGE_SIZE,
          page: params.page || DEFAULT_PAGE,
          sortDirection: params.sortDirection || DEFAULT_SORT,
          search: params.search || '',
        },
      })
      .then(res => ({
        userId: params.userId,
        total: res.total,
        kpiObservations: List(res.results.map(r => new KpiObservation(r))),
      }));
  }

  static create(params: CreateObservationRequest): Promise<void> {
    return observationApi.postJson<CreateObservationRequest, void>('', params);
  }

  static update(id: KpiObservationId, params: UpdateObservationRequest): Promise<void> {
    return observationApi.putJson<UpdateObservationRequest, void>(`/${id}`, params);
  }

  static getReminders(userId: UserId): Promise<List<KpiRequest>> {
    return reminderApi.getJson<KpiRequest[]>(`/${userId}`).then(r => List(r));
  }

  static bySkills(params: ObservationBySkillsRequest): Promise<List<KpiObservation>> {
    return observationApi
      .getJson<ObservationBySkillsResponse>('/by_skills', {
        params: {
          ...params,
          startDate: params.startDate.format(),
          endDate: params.endDate?.format(),
        },
      })
      .then(observations => List(observations.map(r => new KpiObservation(r))));
  }

  static coachingKpis(params: ObservationBySkillsRequest): Promise<List<CoachingKpi>> {
    return observationApi
      .getJson<CoachingKpi[]>(`/coaching_kpis`, {
        params: {
          ...params,
          startDate: params.startDate.format(),
          endDate: params.endDate?.format(),
        },
      })
      .then(goals => List(goals.map(g => new CoachingKpi(g))));
  }
}
